/**
 * 全局变量管理 Hook
 * 提供 Dashboard 相关状态的全局访问和修改能力
 * 所有前端 JS 文件都可以通过这个 Hook 访问和修改这些状态
 */

// 定义分页限制常量，从环境变量读取，默认20
const pageLimit = parseInt(process.env.REACT_APP_FETCH_LIMIT || process.env.FETCH_LIMIT || '20', 10);

// 全局状态存储
let globalState = {
  // 筛选相关
  selectedFolder: 'all', // 缺省指定到 AllNotes，已注释掉 Playground 按键
  selectedTag: null,
  selectedTopic: null,
  selectedKB: null,

  // 搜索相关
  searchKeyword: '',
  searchMode: 'hybrid',

  // 笔记选择相关
  selectedNote: null,
  isUserSelectedNote: false,

  // 笔记状态变化相关
  noteStateChange: {
    noteId: null,
    changeType: null, // 'pin', 'unpin', 'update', 'delete'
    timestamp: null
  },

  // 分页相关
  pagination: {
    total: 0,
    page: 1,
    limit: pageLimit,
    hasMore: false
  },

  // 展开状态相关
  expandedTopicsArray: [],
  expandedCollaborativeKBsArray: [],

  // 待检查的主题（用于主题树自动刷新）
  // 格式: { kbId: [topicId, ...], ... } 或 { private: [topicId, ...] }
  pendingTopicCheck: null,

  // QA 搜索历史记录相关
  qaHistory: [], // QA 问答历史记录
  qaSessionKey: '', // 当前会话 key（基于 kbId/topicId）

  // 监听器列表
  listeners: new Set()
};

/**
 * 全局状态更新函数
 * @param {Object} updates - 要更新的状态对象
 * @param {string} source - 更新来源标识
 */
export const updateGlobalState = (updates, source = 'unknown') => {
  const oldState = { ...globalState };

  // 更新状态
  globalState = {
    ...globalState,
    ...updates
  };

  // 记录更新日志
  console.log(`[useDashboardGlobalVar] 全局状态更新 (来源: ${source}):`, {
    old: oldState,
    new: globalState,
    updates
  });

  // 通知所有监听器
  globalState.listeners.forEach(listener => {
    try {
      // 添加类型检查，确保监听器是函数
      if (typeof listener !== 'function') {
        console.error('[useDashboardGlobalVar] 发现无效监听器:', listener, '类型:', typeof listener);
        // 移除无效监听器
        globalState.listeners.delete(listener);
        return;
      }
      listener(globalState, oldState, updates);
    } catch (error) {
      console.error('[useDashboardGlobalVar] 监听器执行错误:', error);
      // 如果监听器执行出错，也移除它
      globalState.listeners.delete(listener);
    }
  });
};

/**
 * 获取全局状态
 * @param {string|string[]} keys - 要获取的状态键，可以是单个键或键数组
 * @returns {any} 状态值或状态对象
 */
export const getGlobalState = (keys = null) => {
  if (!keys) {
    return { ...globalState };
  }

  if (Array.isArray(keys)) {
    const result = {};
    keys.forEach(key => {
      if (key in globalState) {
        result[key] = globalState[key];
      }
    });
    return result;
  }

  return globalState[keys];
};

/**
 * 设置单个全局状态
 * @param {string} key - 状态键
 * @param {any} value - 状态值
 * @param {string} source - 更新来源标识
 */
export const setGlobalState = (key, value, source = 'unknown') => {
  console.log('[useDashboardGlobalVar] setGlobalState 被调用:', { key, value, source });
  if (key in globalState) {
    console.log('[useDashboardGlobalVar] 状态键存在，调用 updateGlobalState');
    updateGlobalState({ [key]: value }, source);
  } else {
    console.warn(`[useDashboardGlobalVar] 尝试设置不存在的状态键: ${key}`);
  }
};

/**
 * 批量设置全局状态
 * @param {Object} updates - 要更新的状态对象
 * @param {string} source - 更新来源标识
 */
export const setGlobalStates = (updates, source = 'unknown') => {
  updateGlobalState(updates, source);
};

/**
 * 添加状态变化监听器
 * @param {Function} listener - 监听器函数，接收 (newState, oldState, updates) 参数
 * @returns {Function} 移除监听器的函数
 */
export const addGlobalStateListener = (listener) => {
  // 添加类型检查
  if (typeof listener !== 'function') {
    console.error('[useDashboardGlobalVar] 尝试添加无效监听器:', listener, '类型:', typeof listener);
    // 返回一个空的清理函数
    return () => { };
  }

  globalState.listeners.add(listener);

  // 返回移除监听器的函数
  return () => {
    globalState.listeners.delete(listener);
  };
};

/**
 * 移除状态变化监听器
 * @param {Function} listener - 要移除的监听器函数
 */
export const removeGlobalStateListener = (listener) => {
  globalState.listeners.delete(listener);
};

/**
 * 调试函数：获取当前监听器信息
 * @returns {Object} 监听器信息
 */
export const getListenersInfo = () => {
  const listenersArray = Array.from(globalState.listeners);
  return {
    count: listenersArray.length,
    listeners: listenersArray.map((listener, index) => ({
      index,
      type: typeof listener,
      isFunction: typeof listener === 'function',
      toString: listener.toString().substring(0, 100) + '...'
    }))
  };
};

/**
 * 重置全局状态到初始值
 * @param {string} source - 重置来源标识
 */
export const resetGlobalState = (source = 'unknown') => {
  const initialState = {
    selectedFolder: 'all',
    selectedTag: null,
    selectedTopic: null,
    selectedKB: null,
    searchKeyword: '',
    searchMode: 'hybrid',
    selectedNote: null,
    isUserSelectedNote: false,
    pagination: {
      total: 0,
      page: 1,
      limit: pageLimit,
      hasMore: false
    },
    expandedTopicsArray: [],
    expandedCollaborativeKBsArray: [],
    qaHistory: [],
    qaSessionKey: ''
  };

  updateGlobalState(initialState, source);
};

/**
 * 获取状态快照
 * @returns {Object} 当前状态的深拷贝
 */
export const getGlobalStateSnapshot = () => {
  return JSON.parse(JSON.stringify(globalState));
};

/**
 * 从快照恢复状态
 * @param {Object} snapshot - 状态快照
 * @param {string} source - 恢复来源标识
 */
export const restoreGlobalStateFromSnapshot = (snapshot, source = 'unknown') => {
  updateGlobalState(snapshot, source);
};

/**
 * 检查状态是否发生变化
 * @param {Object} oldState - 旧状态
 * @param {Object} newState - 新状态
 * @returns {boolean} 是否发生变化
 */
export const hasStateChanged = (oldState, newState) => {
  return JSON.stringify(oldState) !== JSON.stringify(newState);
};

/**
 * 获取特定状态的变化
 * @param {Object} oldState - 旧状态
 * @param {Object} newState - 新状态
 * @returns {Object} 发生变化的状态
 */
export const getStateChanges = (oldState, newState) => {
  const changes = {};

  Object.keys(newState).forEach(key => {
    if (key === 'listeners') return; // 跳过监听器

    if (JSON.stringify(oldState[key]) !== JSON.stringify(newState[key])) {
      changes[key] = {
        old: oldState[key],
        new: newState[key]
      };
    }
  });

  return changes;
};

/**
 * 导出所有可用的状态键
 */
export const GLOBAL_STATE_KEYS = {
  SELECTED_FOLDER: 'selectedFolder',
  SELECTED_TAG: 'selectedTag',
  SELECTED_TOPIC: 'selectedTopic',
  SELECTED_KB: 'selectedKB',
  SEARCH_KEYWORD: 'searchKeyword',
  SEARCH_MODE: 'searchMode',
  SELECTED_NOTE: 'selectedNote',
  IS_USER_SELECTED_NOTE: 'isUserSelectedNote',
  PAGINATION: 'pagination',
  EXPANDED_TOPICS_ARRAY: 'expandedTopicsArray',
  EXPANDED_COLLABORATIVE_KBS_ARRAY: 'expandedCollaborativeKBsArray',
  NOTE_STATE_CHANGE: 'noteStateChange',
  PENDING_TOPIC_CHECK: 'pendingTopicCheck',
  QA_HISTORY: 'qaHistory',
  QA_SESSION_KEY: 'qaSessionKey'
};

/**
 * 便捷的 getter 函数
 */
export const getSelectedFolder = () => globalState.selectedFolder;
export const getSelectedTag = () => globalState.selectedTag;
export const getSelectedTopic = () => globalState.selectedTopic;
export const getSelectedKB = () => globalState.selectedKB;
export const getSearchKeyword = () => globalState.searchKeyword;
export const getSearchMode = () => globalState.searchMode;
export const getSelectedNote = () => globalState.selectedNote;
export const getIsUserSelectedNote = () => globalState.isUserSelectedNote;
export const getPagination = () => globalState.pagination;
export const getNoteStateChange = () => globalState.noteStateChange;
export const getPendingTopicCheck = () => globalState.pendingTopicCheck;
export const getExpandedTopicsArray = () => {
  const value = globalState.expandedTopicsArray;
  // 确保返回数组
  if (!Array.isArray(value)) {
    console.warn('[useDashboardGlobalVar] expandedTopicsArray 不是数组，重置为空数组:', value);
    globalState.expandedTopicsArray = [];
    return [];
  }
  return value;
};

export const getExpandedCollaborativeKBsArray = () => {
  const value = globalState.expandedCollaborativeKBsArray;
  // 确保返回数组
  if (!Array.isArray(value)) {
    console.warn('[useDashboardGlobalVar] expandedCollaborativeKBsArray 不是数组，重置为空数组:', value);
    globalState.expandedCollaborativeKBsArray = [];
    return [];
  }
  return value;
};

export const getQaHistory = () => {
  const value = globalState.qaHistory;
  // 确保返回数组
  if (!Array.isArray(value)) {
    console.warn('[useDashboardGlobalVar] qaHistory 不是数组，重置为空数组:', value);
    globalState.qaHistory = [];
    return [];
  }
  return value;
};

export const getQaSessionKey = () => globalState.qaSessionKey;

/**
 * 便捷的 setter 函数
 */
export const setSelectedFolder = (value, source = 'unknown') => {
  // 设置文件夹时，重置主题和知识库选择
  updateGlobalState({
    selectedFolder: value,
    selectedTopic: null,
    selectedKB: null
  }, source);
};

export const setSelectedTag = (value, source = 'unknown') => setGlobalState('selectedTag', value, source);

export const setSelectedTopic = (value, source = 'unknown') => {
  // 设置主题时，重置文件夹和知识库选择
  updateGlobalState({
    selectedTopic: value,
    selectedFolder: 'all',
    selectedKB: null
  }, source);
};

export const setSelectedKB = (value, source = 'unknown') => {
  // 设置知识库时，重置文件夹和主题选择
  updateGlobalState({
    selectedKB: value,
    selectedFolder: 'all',
    selectedTopic: null
  }, source);
};

export const setSearchKeyword = (value, source = 'unknown') => {
  console.log('[useDashboardGlobalVar] setSearchKeyword 被调用:', { value, source, stack: new Error().stack });
  setGlobalState('searchKeyword', value, source);
};
export const setSearchMode = (value, source = 'unknown') => setGlobalState('searchMode', value, source);
export const setSelectedNote = (value, source = 'unknown') => setGlobalState('selectedNote', value, source);
export const setIsUserSelectedNote = (value, source = 'unknown') => setGlobalState('isUserSelectedNote', value, source);
export const setPagination = (value, source = 'unknown') => setGlobalState('pagination', value, source);

/**
 * 设置笔记状态变化，用于触发UI刷新
 * @param {string} noteId - 笔记ID
 * @param {string} changeType - 变化类型：'pin', 'unpin', 'update', 'delete'
 * @param {string} source - 更新来源标识
 */
export const setNoteStateChange = (noteId, changeType, source = 'unknown') => {
  console.log('[useDashboardGlobalVar] setNoteStateChange 被调用:', { noteId, changeType, source });
  setGlobalState('noteStateChange', {
    noteId,
    changeType,
    timestamp: Date.now()
  }, source);
  console.log('[useDashboardGlobalVar] setNoteStateChange 调用完成');
};
export const setExpandedTopicsArray = (value, source = 'unknown') => {
  // 确保设置的是数组
  if (!Array.isArray(value)) {
    console.warn('[useDashboardGlobalVar] setExpandedTopicsArray 接收到非数组值，转换为数组:', value);
    value = [];
  }
  setGlobalState('expandedTopicsArray', value, source);
};

export const setExpandedCollaborativeKBsArray = (value, source = 'unknown') => {
  // 确保设置的是数组
  if (!Array.isArray(value)) {
    console.warn('[useDashboardGlobalVar] setExpandedCollaborativeKBsArray 接收到非数组值，转换为数组:', value);
    value = [];
  }
  setGlobalState('expandedCollaborativeKBsArray', value, source);
};

export const setQaHistory = (value, source = 'unknown') => {
  // 确保设置的是数组
  if (!Array.isArray(value)) {
    console.warn('[useDashboardGlobalVar] setQaHistory 接收到非数组值，转换为数组:', value);
    value = [];
  }
  setGlobalState('qaHistory', value, source);
};

export const setQaSessionKey = (value, source = 'unknown') => setGlobalState('qaSessionKey', value, source);

/**
 * 设置待检查的主题列表（用于主题树自动刷新）
 * @param {Object} value - 格式: { kbId: [topicId, ...], ... } 或 { private: [topicId, ...] }
 * @param {string} source - 更新来源标识
 */
export const setPendingTopicCheck = (value, source = 'unknown') => {
  console.log('[useDashboardGlobalVar] setPendingTopicCheck:', value);
  setGlobalState('pendingTopicCheck', value, source);
};

/**
 * 清空待检查的主题列表
 * @param {string} source - 更新来源标识
 */
export const clearPendingTopicCheck = (source = 'unknown') => {
  console.log('[useDashboardGlobalVar] clearPendingTopicCheck');
  setGlobalState('pendingTopicCheck', null, source);
};

/**
 * 设置互斥的选择状态
 * 这三个值互斥：selectedFolder、selectedTopic、selectedKB
 * 设置其中一个值时，另外两个值会被重置为默认值
 * @param {Object} selection - 选择对象，包含要设置的值
 * @param {string} source - 更新来源标识
 */
export const setMutualExclusiveSelection = (selection, source = 'unknown') => {
  const { selectedFolder, selectedTopic, selectedKB } = selection;

  // 构建更新对象，只包含提供的值
  const updates = {};


  if (selectedTopic !== undefined) {
    updates.selectedTopic = selectedTopic;
    updates.selectedFolder = undefined; //'all';
    updates.selectedKB = null;
  } else if (selectedKB !== undefined) {
    updates.selectedKB = selectedKB;
    updates.selectedFolder = undefined; //'all';
    updates.selectedTopic = null;
  } else if (selectedFolder !== undefined) {
    updates.selectedFolder = selectedFolder;
    updates.selectedTopic = null;
    updates.selectedKB = null;
  } else {
    updates.selectedFolder = 'all';
    updates.selectedTopic = null;
    updates.selectedKB = null;
  }

  if (Object.keys(updates).length > 0) {
    updateGlobalState(updates, source);
  }
};

/**
 * 默认导出
 */
export default {
  // 主要函数
  updateGlobalState,
  getGlobalState,
  setGlobalState,
  setGlobalStates,
  addGlobalStateListener,
  removeGlobalStateListener,
  resetGlobalState,
  getGlobalStateSnapshot,
  restoreGlobalStateFromSnapshot,
  hasStateChanged,
  getStateChanges,

  // 常量
  GLOBAL_STATE_KEYS,

  // 便捷 getter
  getSelectedFolder,
  getSelectedTag,
  getSelectedTopic,
  getSelectedKB,
  getSearchKeyword,
  getSearchMode,
  getSelectedNote,
  getIsUserSelectedNote,
  getPagination,
  getNoteStateChange,
  getExpandedTopicsArray,
  getExpandedCollaborativeKBsArray,
  getPendingTopicCheck,
  getQaHistory,
  getQaSessionKey,

  // 便捷 setter
  setSelectedFolder,
  setSelectedTag,
  setSelectedTopic,
  setSelectedKB,
  setSearchKeyword,
  setSearchMode,
  setSelectedNote,
  setIsUserSelectedNote,
  setPagination,
  setNoteStateChange,
  setExpandedTopicsArray,
  setExpandedCollaborativeKBsArray,
  setPendingTopicCheck,
  clearPendingTopicCheck,
  setMutualExclusiveSelection,
  setQaHistory,
  setQaSessionKey
};
